import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search, Printer, Save, FileText, X, Plus, Wifi, Bluetooth,
  ShoppingBag, User, Building2, Mail, Hash, Calendar, Percent,
  CheckCircle, Send, XCircle, Clock, Trash2, FolderOpen,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { r2Url } from '@/lib/r2';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { getComponentSortRank } from '@/types/database';

// ── Category helpers ──────────────────────────────────────────────────────────
const categoryRank = (cat: string) => getComponentSortRank(cat ?? '');

const canonicalCategory = (cat: string): string => {
  const lower = cat?.toLowerCase() ?? '';
  if (['cpu cooler', 'fan'].includes(lower)) return 'cpu cooler';
  if (lower === 'coo') return 'coo';
  if (['storage', 'rom'].includes(lower)) return 'storage';
  if (['case', 'cas'].includes(lower)) return 'case';
  if (['accessories', 'acc'].includes(lower)) return 'accessories';
  return lower;
};

const CATEGORY_LABELS: Record<string, string> = {
  cpu: 'Processor', 'cpu cooler': 'CPU Cooler', coo: 'Case Cooling', mb: 'Motherboard',
  ram: 'Memory', storage: 'Storage', gpu: 'Graphics Card', psu: 'Power Supply',
  os: 'Operating System', case: 'Case', accessories: 'Accessories',
};

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('en-MT', { style: 'currency', currency: 'EUR' }).format(val);

import { EPS_PLANS, calcEps, calcRrp } from '@/lib/pricing';

// ── Types ─────────────────────────────────────────────────────────────────────
type QuoteType   = 'b2c' | 'b2b';
type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';

const STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: 'Draft', sent: 'Sent', accepted: 'Accepted', rejected: 'Rejected', expired: 'Expired',
};
const STATUS_CLASS: Record<QuoteStatus, string> = {
  draft:    'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  sent:     'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  accepted: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  expired:  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};

interface BuildOption {
  id: string;
  sku: string;
  description: string;
  status: string;
  rrp: number | null;
}

interface BomItem {
  component_sku: string;
  component_type: string;
  qty_per_build: number;
  cost_ex_vat: number;
  description?: string;
  category?: string;
}

interface AddonComponent {
  sku: string;
  description: string;
  price: number;
}

interface AddonItem {
  id: string;        // local key only
  sku: string;
  description: string;
  price: number;
  qty: number;
}

interface CompPickerResult {
  part_number: string;
  description: string;
  category: string;
  current_price: number;
  available_qty: number | null;
  on_order_qty: number | null;
}

interface QuoteListItem {
  id: string;
  quote_number: string;
  status: QuoteStatus;
  customer_name: string | null;
  customer_company: string | null;
  reference: string | null;
  quote_type: QuoteType;
  build_ids: string[];
  created_at: string;
  updated_at: string;
}

// ── Session persistence ───────────────────────────────────────────────────────
const MAX_BUILDS = 4;
// Use v2 key so old sessions don't break the new shape
const QUOTE_SESSION_KEY = 'quote-session-v2';

interface QuoteSession {
  selectedIds: string[];
  quoteType: QuoteType;
  showPrice: boolean;
  showEps: boolean;
  showSpecs: boolean;
  showCodes: boolean;
  etaWeeks: string;
  customerName: string;
  customerCompany: string;
  customerEmail: string;
  reference: string;
  validUntil: string;
  discountPct: number;
  notes: string;
  qtyPerBuild: Record<string, number>;
  wifiAddons: Record<string, boolean>;
  btAddons: Record<string, boolean>;
  addons: AddonItem[];
  quoteId: string | null;
  quoteNumber: string | null;
  quoteStatus: QuoteStatus;
}

function defaultValidUntil(): string {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().split('T')[0];
}

function loadSession(): Partial<QuoteSession> {
  try {
    const raw = localStorage.getItem(QUOTE_SESSION_KEY);
    return raw ? (JSON.parse(raw) as Partial<QuoteSession>) : {};
  } catch {
    return {};
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
export function QuotePage() {
  const queryClient = useQueryClient();
  const s = loadSession();

  // ── Existing display state ────────────────────────────────────────────────
  const [selectedIds, setSelectedIds]       = useState<string[]>(s.selectedIds ?? []);
  const [searchInput, setSearchInput]       = useState('');
  const [buildPickerOpen, setBuildPickerOpen] = useState(false);
  const [quoteType, setQuoteType]           = useState<QuoteType>(s.quoteType ?? 'b2c');
  const [editedDescs, setEditedDescs]       = useState<Record<string, string>>({});
  const [editedBuildDescs, setEditedBuildDescs] = useState<Record<string, string>>({});
  const [showPrice, setShowPrice]           = useState(s.showPrice ?? true);
  const [showCodes, setShowCodes]           = useState(s.showCodes ?? false);
  const [showEps, setShowEps]               = useState(s.showEps ?? false);
  const [showSpecs, setShowSpecs]           = useState(s.showSpecs ?? false);
  const [etaWeeks, setEtaWeeks]             = useState(s.etaWeeks ?? '');
  const [hiddenSkus, setHiddenSkus]         = useState<Record<string, Set<string>>>({});
  const [dirty, setDirty]                   = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  // ── Customer & quote meta ─────────────────────────────────────────────────
  const [customerName,    setCustomerName]    = useState(s.customerName    ?? '');
  const [customerCompany, setCustomerCompany] = useState(s.customerCompany ?? '');
  const [customerEmail,   setCustomerEmail]   = useState(s.customerEmail   ?? '');
  const [reference,       setReference]       = useState(s.reference       ?? '');
  const [validUntil,      setValidUntil]      = useState(s.validUntil      ?? defaultValidUntil());
  const [discountPct,     setDiscountPct]     = useState<number>(s.discountPct ?? 0);
  const [notes,           setNotes]           = useState(s.notes           ?? '');

  // ── Per-build extras ──────────────────────────────────────────────────────
  const [qtyPerBuild, setQtyPerBuild] = useState<Record<string, number>>(s.qtyPerBuild ?? {});
  const [wifiAddons,  setWifiAddons]  = useState<Record<string, boolean>>(s.wifiAddons ?? {});
  const [btAddons,    setBtAddons]    = useState<Record<string, boolean>>(s.btAddons   ?? {});

  // ── Global add-ons ────────────────────────────────────────────────────────
  const [addons, setAddons] = useState<AddonItem[]>(s.addons ?? []);

  // ── Quote save state ──────────────────────────────────────────────────────
  const [quoteId,     setQuoteId]     = useState<string | null>(s.quoteId     ?? null);
  const [quoteNumber, setQuoteNumber] = useState<string | null>(s.quoteNumber ?? null);
  const [quoteStatus, setQuoteStatus] = useState<QuoteStatus>(s.quoteStatus   ?? 'draft');

  // ── Component picker (shared for WiFi / BT / add-ons) ────────────────────
  const [compPickerOpen,    setCompPickerOpen]    = useState(false);
  const [compPickerSearch,  setCompPickerSearch]  = useState('');

  // ── Quote picker (load saved quote) ──────────────────────────────────────
  const [quotePickerOpen,   setQuotePickerOpen]   = useState(false);
  const [quotePickerSearch, setQuotePickerSearch] = useState('');

  // ── Persist session ───────────────────────────────────────────────────────
  useEffect(() => {
    const session: QuoteSession = {
      selectedIds, quoteType, showPrice, showEps, showSpecs, showCodes, etaWeeks,
      customerName, customerCompany, customerEmail, reference, validUntil,
      discountPct, notes, qtyPerBuild,
      // Convert Sets to arrays for serialisation
      wifiAddons, btAddons, addons, quoteId, quoteNumber, quoteStatus,
    };
    localStorage.setItem(QUOTE_SESSION_KEY, JSON.stringify(session));
  }, [
    selectedIds, quoteType, showPrice, showEps, showSpecs, showCodes, etaWeeks,
    customerName, customerCompany, customerEmail, reference, validUntil,
    discountPct, notes, qtyPerBuild, wifiAddons, btAddons, addons,
    quoteId, quoteNumber, quoteStatus,
  ]);

  // ── Visibility helpers ────────────────────────────────────────────────────
  const isVisible = (buildId: string, sku: string) => !hiddenSkus[buildId]?.has(sku);
  const toggleVisible = (buildId: string, sku: string) => {
    setHiddenSkus(prev => {
      const set = new Set(prev[buildId] ?? []);
      if (set.has(sku)) set.delete(sku); else set.add(sku);
      return { ...prev, [buildId]: set };
    });
  };

  // ── Queries: builds list ──────────────────────────────────────────────────
  const { data: allBuilds = [] } = useQuery({
    queryKey: ['quote-builds-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('builds')
        .select('id, sku, description, status, rrp')
        .order('sku');
      if (error) throw error;
      return (data ?? []) as BuildOption[];
    },
  });

  // ── Queries: BOM ──────────────────────────────────────────────────────────
  const { data: bomData = {} } = useQuery({
    queryKey: ['quote-bom', selectedIds],
    queryFn: async () => {
      if (selectedIds.length === 0) return {};
      const selectedSkus: Record<string, string> = {};
      selectedIds.forEach(id => {
        const b = allBuilds.find(x => x.id === id);
        if (b) selectedSkus[id] = b.sku;
      });

      const { data: bomRows } = await supabase
        .from('build_components')
        .select('build_id, component_sku, component_type, qty_per_build, cost_ex_vat')
        .in('build_id', selectedIds);

      const buildsWithBom = new Set((bomRows ?? []).map(r => r.build_id));
      const buildsNeedFallback = selectedIds.filter(id => !buildsWithBom.has(id));

      let fallbackComponents: any[] = [];
      if (buildsNeedFallback.length > 0) {
        const skusToSearch = buildsNeedFallback.map(id => selectedSkus[id]).filter(Boolean);
        if (skusToSearch.length > 0) {
          const orFilter = skusToSearch.map(sku => `build_sku.ilike.%${sku}%`).join(',');
          const { data: fbData } = await supabase
            .from('components')
            .select('part_number, description, category, current_price, build_sku')
            .or(orFilter);
          fallbackComponents = fbData ?? [];
        }
      }

      const bcSkus = [...new Set((bomRows ?? []).map(r => r.component_sku).filter(Boolean))];
      const descMap: Record<string, { description: string; category: string; price: number }> = {};
      if (bcSkus.length > 0) {
        const { data: compData } = await supabase
          .from('components')
          .select('part_number, description, category, current_price')
          .in('part_number', bcSkus);
        (compData ?? []).forEach((c: any) => {
          descMap[c.part_number] = { description: c.description || '', category: c.category || '', price: c.current_price ?? 0 };
        });
      }

      const result: Record<string, BomItem[]> = {};
      (bomRows ?? []).forEach((r: any) => {
        if (!result[r.build_id]) result[r.build_id] = [];
        result[r.build_id].push({
          ...r,
          cost_ex_vat: r.cost_ex_vat || descMap[r.component_sku]?.price || 0,
          description: descMap[r.component_sku]?.description || '',
          category: descMap[r.component_sku]?.category || r.component_type || '',
        });
      });

      for (const buildId of buildsNeedFallback) {
        const sku = selectedSkus[buildId];
        if (!sku) continue;
        const matched = fallbackComponents.filter(c => {
          const entries = (c.build_sku ?? '').split(',').map((s: string) => s.trim().replace(/-EOL$/i, ''));
          return entries.includes(sku);
        });
        result[buildId] = matched.map(c => ({
          build_id: buildId,
          component_sku: c.part_number,
          component_type: c.category || '',
          qty_per_build: 1,
          cost_ex_vat: c.current_price ?? 0,
          description: c.description || '',
          category: c.category || '',
        }));
      }

      Object.values(result).forEach(items =>
        items.sort((a, b) => categoryRank(a.category || '') - categoryRank(b.category || ''))
      );
      return result;
    },
    enabled: selectedIds.length > 0 && allBuilds.length > 0,
  });

  // ── Queries: display names ────────────────────────────────────────────────
  const allComponentSkus = useMemo(() => {
    const skus = new Set<string>();
    selectedIds.forEach(id => (bomData[id] ?? []).forEach(item => skus.add(item.component_sku)));
    return [...skus];
  }, [selectedIds, bomData]);

  const { data: savedNames = {} } = useQuery({
    queryKey: ['display-names', allComponentSkus],
    queryFn: async () => {
      if (allComponentSkus.length === 0) return {};
      const { data } = await supabase
        .from('component_display_names')
        .select('part_number, display_name_b2c, display_name_b2b')
        .in('part_number', allComponentSkus);
      const map: Record<string, { b2c: string | null; b2b: string | null }> = {};
      (data ?? []).forEach((r: any) => {
        map[r.part_number] = { b2c: r.display_name_b2c, b2b: r.display_name_b2b };
      });
      return map;
    },
    enabled: allComponentSkus.length > 0,
  });

  // ── Queries: component specs ──────────────────────────────────────────────
  const { data: componentSpecsData = {} } = useQuery({
    queryKey: ['quote-component-specs', allComponentSkus],
    queryFn: async () => {
      if (allComponentSkus.length === 0) return {};
      const { data } = await supabase
        .from('components')
        .select('part_number, category, specs')
        .in('part_number', allComponentSkus);
      const map: Record<string, { category: string; specs: Record<string, any> }> = {};
      (data ?? []).forEach((c: any) => {
        map[c.part_number] = { category: c.category || '', specs: c.specs || {} };
      });
      return map;
    },
    enabled: allComponentSkus.length > 0,
  });

  const { data: specsSchemaMap = {} } = useQuery({
    queryKey: ['component-types-schema-quote'],
    queryFn: async () => {
      const { data } = await supabase.from('component_types').select('code, specs_schema');
      const map: Record<string, any[]> = {};
      (data ?? []).forEach((t: any) => { map[t.code] = t.specs_schema?.fields ?? []; });
      return map;
    },
  });

  // ── Queries: media ────────────────────────────────────────────────────────
  const { data: mediaData = {} } = useQuery({
    queryKey: ['quote-media', selectedIds],
    queryFn: async () => {
      if (selectedIds.length === 0) return {};
      const result: Record<string, string | null> = {};
      const { data: buildMedia } = await supabase
        .from('build_media')
        .select('build_id, file_path')
        .in('build_id', selectedIds)
        .order('created_at', { ascending: true });

      const covered = new Set<string>();
      (buildMedia ?? []).forEach((m: any) => {
        if (!covered.has(m.build_id)) {
          covered.add(m.build_id);
          result[m.build_id] = r2Url(`build-media/${m.file_path}`);
        }
      });

      const uncovered = selectedIds.filter(id => !covered.has(id));
      if (uncovered.length > 0 && Object.keys(bomData).length > 0) {
        for (const buildId of uncovered) {
          const caseItem = (bomData[buildId] ?? []).find(b => ['case', 'cas'].includes(b.category?.toLowerCase() ?? ''));
          if (caseItem) {
            const { data: comp } = await supabase.from('components').select('id').eq('part_number', caseItem.component_sku).single();
            if (comp) {
              const { data: cMedia } = await supabase.from('component_media').select('file_path').eq('component_id', comp.id).order('created_at', { ascending: true }).limit(1);
              if (cMedia?.length) {
                result[buildId] = r2Url(`build-media/${cMedia[0].file_path}`);
              }
            }
          }
        }
      }
      return result;
    },
    enabled: selectedIds.length > 0 && Object.keys(bomData).length > 0,
  });

  // ── Queries: saved quotes list ────────────────────────────────────────────
  const { data: savedQuotes = [] } = useQuery({
    queryKey: ['saved-quotes-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quotes')
        .select('id, quote_number, status, customer_name, customer_company, reference, quote_type, build_ids, created_at, updated_at')
        .order('updated_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as QuoteListItem[];
    },
    enabled: quotePickerOpen,
  });

  // ── Queries: component picker search ──────────────────────────────────────
  const { data: compPickerResults = [] } = useQuery({
    queryKey: ['comp-picker', compPickerSearch],
    queryFn: async () => {
      const q = compPickerSearch.trim();
      if (q.length === 0) return [];
      const { data } = await supabase
        .from('components')
        .select('part_number, description, category, current_price, available_qty, on_order_qty')
        .or(`description.ilike.%${q}%,part_number.ilike.%${q}%,category.ilike.%${q}%`)
        .order('description')
        .limit(25);
      return (data ?? []) as CompPickerResult[];
    },
    enabled: compPickerOpen && compPickerSearch.trim().length >= 1,
  });

  // ── Load saved names into editedDescs ─────────────────────────────────────
  useEffect(() => {
    if (Object.keys(savedNames).length === 0) return;
    const field = quoteType === 'b2c' ? 'b2c' : 'b2b';
    const newDescs: Record<string, string> = {};
    for (const [sku, names] of Object.entries(savedNames)) {
      const saved = names[field];
      if (saved && !(sku in editedDescs)) newDescs[sku] = saved;
    }
    if (Object.keys(newDescs).length > 0) {
      setEditedDescs(prev => ({ ...newDescs, ...prev }));
    }
  }, [savedNames, quoteType]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchQuoteType = (type: QuoteType) => {
    setQuoteType(type);
    const field = type === 'b2c' ? 'b2c' : 'b2b';
    const newDescs: Record<string, string> = {};
    for (const [sku, names] of Object.entries(savedNames)) {
      const saved = names[field];
      if (saved) newDescs[sku] = saved;
    }
    setEditedDescs(newDescs);
    setDirty(false);
  };

  // ── Desc helpers ──────────────────────────────────────────────────────────
  const getCompDesc  = (sku: string, original: string) => editedDescs[sku] ?? original;
  const setCompDesc  = (sku: string, value: string)    => { setEditedDescs(prev => ({ ...prev, [sku]: value })); setDirty(true); };
  const getBuildDesc = (buildId: string, original: string) => editedBuildDescs[buildId] ?? original;

  // ── Specs helper ──────────────────────────────────────────────────────────
  const QUOTE_CAT_TO_TYPE: Record<string, string> = {
    cpu: 'CPU', 'cpu cooler': 'FAN', fan: 'FAN', coo: 'COO',
    mb: 'MB', ram: 'RAM', storage: 'STORAGE', rom: 'STORAGE',
    gpu: 'GPU', psu: 'PSU', case: 'CASE', cas: 'CASE',
    os: 'OS', acc: 'ACC', accessories: 'ACC',
  };

  interface SpecEntry { text: string; missing: boolean }

  const getSpecEntries = useCallback((sku: string): { entries: SpecEntry[]; allMissing: boolean } => {
    const compData = componentSpecsData[sku];
    if (!compData) return { entries: [], allMissing: false };
    const typeCode = QUOTE_CAT_TO_TYPE[compData.category.toLowerCase()] || compData.category.toUpperCase();
    const schema = specsSchemaMap[typeCode] ?? [];
    if (schema.length === 0) return { entries: [], allMissing: false };
    const hasAnySpecs = compData.specs && Object.keys(compData.specs).length > 0;
    const entries = schema.map((f: any) => {
      const val = compData.specs?.[f.key];
      if (val == null || val === '') return { text: `${f.label}: —`, missing: true };
      if (typeof val === 'boolean') return { text: `${f.label}: ${val ? 'Yes' : 'No'}`, missing: false };
      if (Array.isArray(val)) return { text: `${f.label}: ${val.join(', ')}`, missing: false };
      if (f.unit) return { text: `${val} ${f.unit}`, missing: false };
      return { text: String(val), missing: false };
    });
    return { entries, allMissing: !hasAnySpecs };
  }, [componentSpecsData, specsSchemaMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pricing ───────────────────────────────────────────────────────────────
  const buildTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    selectedIds.forEach(id => {
      totals[id] = (bomData[id] ?? []).reduce((sum, item) => sum + (item.cost_ex_vat ?? 0) * (item.qty_per_build ?? 1), 0);
    });
    return totals;
  }, [selectedIds, bomData]);

  const pricingSummary = useMemo(() => {
    const selectedBuilds = selectedIds.map(id => allBuilds.find(b => b.id === id)).filter(Boolean) as BuildOption[];
    let subtotal = 0;
    const buildLines = selectedBuilds.map(build => {
      const rrp     = calcRrp(build.rrp, buildTotals[build.id] ?? 0);
      const qty     = qtyPerBuild[build.id] ?? 1;
      const wifi    = wifiAddons[build.id] ?? false;
      const bt      = btAddons[build.id]   ?? false;
      const lineBase  = rrp * qty;
      subtotal += lineBase;
      return { build, rrp, qty, wifi, bt, lineBase };
    });
    const addonsSubtotal = addons.reduce((sum, a) => sum + a.price * a.qty, 0);
    const discountAmount = subtotal * (discountPct / 100);
    const grandTotal = subtotal - discountAmount;
    return { buildLines, addonsSubtotal, subtotal, discountAmount, grandTotal };
  }, [selectedIds, allBuilds, buildTotals, qtyPerBuild, wifiAddons, btAddons, addons, discountPct]);

  const selectedBuilds = selectedIds.map(id => allBuilds.find(b => b.id === id)).filter(Boolean) as BuildOption[];

  // ── Build picker ──────────────────────────────────────────────────────────
  const addBuild = (id: string) => {
    if (selectedIds.includes(id) || selectedIds.length >= MAX_BUILDS) return;
    setSelectedIds(prev => [...prev, id]);
    setSearchInput('');
    setBuildPickerOpen(false);
  };
  const removeBuild = (index: number) => setSelectedIds(prev => prev.filter((_, i) => i !== index));

  // ── Component picker ──────────────────────────────────────────────────────
  const openCompPicker = () => {
    setCompPickerSearch('');
    setCompPickerOpen(true);
  };

  const selectComponent = (comp: CompPickerResult) => {
    const item: AddonComponent = { sku: comp.part_number, description: comp.description, price: comp.current_price ?? 0 };
    setAddons(prev => [...prev, { ...item, id: crypto.randomUUID(), qty: 1 }]);
    setCompPickerOpen(false);
    setCompPickerSearch('');
  };

  // ── Mutations: save display names ─────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async () => {
      const field = quoteType === 'b2c' ? 'display_name_b2c' : 'display_name_b2b';
      const upserts = Object.entries(editedDescs).map(([sku, name]) => ({
        part_number: sku, [field]: name, updated_at: new Date().toISOString(),
      }));
      if (upserts.length === 0) return;
      const { error } = await supabase
        .from('component_display_names')
        .upsert(upserts, { onConflict: 'part_number', ignoreDuplicates: false });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['display-names'] });
      setDirty(false);
      toast.success(`${quoteType.toUpperCase()} display names saved`);
    },
    onError: (e: any) => toast.error(e.message || 'Failed to save display names'),
  });

  // ── Mutations: save quote to DB ───────────────────────────────────────────
  const saveQuoteMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        status: quoteStatus,
        customer_name:     customerName     || null,
        customer_company:  customerCompany  || null,
        customer_email:    customerEmail    || null,
        reference:         reference        || null,
        quote_type:        quoteType,
        valid_until:       validUntil       || null,
        eta_weeks:         etaWeeks ? parseInt(etaWeeks) : null,
        notes:             notes            || null,
        discount_pct:      discountPct,
        build_ids:         selectedIds,
        qty_per_build:     qtyPerBuild,
        wifi_addons:       wifiAddons,
        bt_addons:         btAddons,
        addons,
        show_price:        showPrice,
        show_codes:        showCodes,
        show_eps:          showEps,
        show_specs:        showSpecs,
        hidden_skus:       Object.fromEntries(Object.entries(hiddenSkus).map(([k, v]) => [k, [...v]])),
        edited_descs:      editedDescs,
        edited_build_descs: editedBuildDescs,
      };

      if (quoteId) {
        const { data, error } = await supabase
          .from('quotes').update(payload).eq('id', quoteId)
          .select('id, quote_number, status').single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('quotes').insert({ ...payload, quote_number: '' })
          .select('id, quote_number, status').single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: (data: any) => {
      setQuoteId(data.id);
      setQuoteNumber(data.quote_number);
      setQuoteStatus(data.status as QuoteStatus);
      queryClient.invalidateQueries({ queryKey: ['saved-quotes-list'] });
      toast.success(`Quote ${data.quote_number} saved`);
    },
    onError: (e: any) => toast.error(e.message || 'Failed to save quote'),
  });

  // ── Mutations: update status ──────────────────────────────────────────────
  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: QuoteStatus) => {
      if (!quoteId) throw new Error('Save the quote first');
      const update: Record<string, any> = { status: newStatus };
      if (newStatus === 'sent') {
        // Reopening from a terminal state: keep original sent_at, clear responded_at.
        // Fresh send from draft: stamp sent_at.
        if (quoteStatus === 'draft') update.sent_at = new Date().toISOString();
        else update.responded_at = null;
      }
      if (['accepted', 'rejected', 'expired'].includes(newStatus)) {
        update.responded_at = new Date().toISOString();
      }
      const { data, error } = await supabase
        .from('quotes').update(update).eq('id', quoteId)
        .select('id, quote_number, status').single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      setQuoteStatus(data.status as QuoteStatus);
      toast.success(`Quote marked as ${data.status}`);
      // Re-save full quote so the DB record is in sync
      saveQuoteMutation.mutate();
    },
    onError: (e: any) => toast.error(e.message || 'Failed to update status'),
  });

  // ── Load saved quote ──────────────────────────────────────────────────────
  const loadQuote = async (id: string) => {
    const { data, error } = await supabase
      .from('quotes')
      .select('*')
      .eq('id', id)
      .single();
    if (error || !data) {
      toast.error(error?.message || 'Failed to load quote');
      return;
    }
    setQuoteId(data.id);
    setQuoteNumber(data.quote_number);
    setQuoteStatus((data.status ?? 'draft') as QuoteStatus);
    setCustomerName(data.customer_name ?? '');
    setCustomerCompany(data.customer_company ?? '');
    setCustomerEmail(data.customer_email ?? '');
    setReference(data.reference ?? '');
    setQuoteType((data.quote_type ?? 'b2c') as QuoteType);
    setValidUntil(data.valid_until ?? defaultValidUntil());
    setEtaWeeks(data.eta_weeks != null ? String(data.eta_weeks) : '');
    setNotes(data.notes ?? '');
    setDiscountPct(Number(data.discount_pct ?? 0));
    setSelectedIds((data.build_ids ?? []) as string[]);
    setQtyPerBuild((data.qty_per_build ?? {}) as Record<string, number>);
    setWifiAddons((data.wifi_addons ?? {}) as Record<string, boolean>);
    setBtAddons((data.bt_addons ?? {}) as Record<string, boolean>);
    setAddons((data.addons ?? []) as AddonItem[]);
    setShowPrice(data.show_price ?? true);
    setShowCodes(data.show_codes ?? false);
    setShowEps(data.show_eps ?? false);
    setShowSpecs(data.show_specs ?? false);
    const hidden: Record<string, Set<string>> = {};
    Object.entries((data.hidden_skus ?? {}) as Record<string, string[]>).forEach(([k, v]) => {
      hidden[k] = new Set(v);
    });
    setHiddenSkus(hidden);
    setEditedDescs((data.edited_descs ?? {}) as Record<string, string>);
    setEditedBuildDescs((data.edited_build_descs ?? {}) as Record<string, string>);
    setDirty(false);
    setQuotePickerOpen(false);
    setQuotePickerSearch('');
    toast.success(`Loaded quote ${data.quote_number}`);
  };

  // ── New quote helper ──────────────────────────────────────────────────────
  const resetQuote = () => {
    setSelectedIds([]); setSearchInput('');
    setCustomerName(''); setCustomerCompany(''); setCustomerEmail('');
    setReference(''); setValidUntil(defaultValidUntil()); setDiscountPct(0); setNotes('');
    setQtyPerBuild({}); setWifiAddons({}); setBtAddons({}); setAddons([]);
    setHiddenSkus({}); setEditedDescs({}); setEditedBuildDescs({});
    setQuoteId(null); setQuoteNumber(null); setQuoteStatus('draft');
    setDirty(false);
    localStorage.removeItem(QUOTE_SESSION_KEY);
  };

  // ── Print ─────────────────────────────────────────────────────────────────
  const handlePrint = useCallback(() => {
    if (!printRef.current) return;
    const printContent = printRef.current.innerHTML;
    const win = window.open('', '_blank', 'width=1200,height=900');
    if (!win) return;

    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>${quoteNumber ?? 'Quote'}</title>
  <style>
    @page { size: portrait; margin: 15mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 12px; color: #1a1a1a; }
    .quote-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #e5e7eb; }
    .quote-logo { max-height: 48px; display: block; }
    .quote-meta { text-align: right; }
    .quote-number { font-size: 18px; font-weight: 700; letter-spacing: 0.05em; color: #111; }
    .quote-date { font-size: 10px; color: #6b7280; margin-top: 2px; }
    .customer-block { margin-bottom: 20px; padding: 12px 16px; background: #f9fafb; border-radius: 6px; border: 1px solid #e5e7eb; display: flex; gap: 48px; }
    .customer-block .col { flex: 1; }
    .customer-block .label { font-size: 10px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 2px; }
    .customer-block .value { font-size: 12px; color: #111; font-weight: 500; }
    .customer-block .value-light { font-size: 11px; color: #6b7280; }
    .build-section { margin-bottom: 28px; page-break-inside: avoid; }
    .build-title { display: flex; align-items: center; gap: 16px; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb; }
    .build-title img { width: 80px; height: 80px; object-fit: cover; border-radius: 8px; }
    .build-title .info { flex: 1; }
    .build-title .sku { font-family: monospace; font-weight: 700; font-size: 15px; }
    .build-title .desc { font-size: 11px; color: #6b7280; margin-top: 2px; }
    .build-title .price-block { text-align: right; }
    .build-title .price { font-size: 20px; font-weight: 700; }
    .build-title .price-label { font-size: 10px; color: #6b7280; }
    .no-image { width: 80px; height: 80px; background: #f3f4f6; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 9px; color: #9ca3af; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
    th { text-align: left; padding: 5px 8px; background: #f9fafb; border: 1px solid #e5e7eb; font-weight: 600; font-size: 10px; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; }
    td { padding: 7px 8px; border: 1px solid #e5e7eb; vertical-align: top; }
    .cat-cell { font-weight: 600; color: #374151; width: 110px; white-space: nowrap; }
    .addon-cat { font-weight: 600; color: #6366f1; font-style: italic; }
    .desc-cell { color: #1a1a1a; }
    .spec-line { font-size: 10px; color: #6b7280; margin-top: 2px; }
    .code-cell { font-family: monospace; font-size: 10px; color: #9ca3af; }
    .qty-cell { font-size: 11px; color: #374151; text-align: center; width: 40px; font-weight: 600; }
    .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #374151; margin: 18px 0 6px; }
    .summary-table td { border: none; padding: 3px 8px; }
    .summary-table .label { color: #6b7280; }
    .summary-table .amount { text-align: right; font-weight: 600; font-family: monospace; }
    .summary-table .total-row td { font-size: 14px; font-weight: 700; border-top: 2px solid #1a1a1a; padding-top: 6px; }
    .summary-table .discount-row td { color: #16a34a; }
    .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #9ca3af; text-align: center; }
    .eps-row { font-size: 10px; color: #6b7280; }
    .eps-row strong { color: #111; }
  </style>
</head>
<body>${printContent}</body>
</html>`);
    win.document.close();

    const images = win.document.querySelectorAll('img');
    let loaded = 0;
    const total = images.length;
    let printed = false;
    const doPrint = () => { if (printed) return; printed = true; win.focus(); win.print(); };
    if (total === 0) {
      setTimeout(doPrint, 100);
    } else {
      images.forEach(img => { img.onload = img.onerror = () => { loaded++; if (loaded >= total) setTimeout(doPrint, 100); }; });
      setTimeout(doPrint, 3000);
    }
  }, [quoteNumber]);  

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <FileText className="h-7 w-7" />
              Customer Quote
            </h1>
            <p className="text-muted-foreground text-sm">Generate and save customer-ready quotes</p>
          </div>
          {quoteNumber && (
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-base font-semibold text-primary border border-primary/30 bg-primary/5 px-2.5 py-0.5 rounded-md">
                {quoteNumber}
              </span>
              <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', STATUS_CLASS[quoteStatus])}>
                {STATUS_LABEL[quoteStatus]}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* B2C / B2B toggle */}
          <div className="flex rounded-lg border overflow-hidden">
            {(['b2c', 'b2b'] as const).map(t => (
              <button key={t}
                className={cn('px-3 py-1.5 text-sm font-medium transition-colors',
                  quoteType === t ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted')}
                onClick={() => switchQuoteType(t)}>
                {t.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Save names */}
          {dirty && (
            <Button size="sm" variant="outline" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} className="gap-1.5">
              <Save className="h-4 w-4" />
              {saveMutation.isPending ? 'Saving…' : 'Save Names'}
            </Button>
          )}

          {/* Save quote */}
          {selectedIds.length > 0 && (
            <Button size="sm" onClick={() => saveQuoteMutation.mutate()} disabled={saveQuoteMutation.isPending} className="gap-1.5">
              <Save className="h-4 w-4" />
              {saveQuoteMutation.isPending ? 'Saving…' : quoteId ? 'Update Quote' : 'Save Quote'}
            </Button>
          )}

          {/* Load saved quote */}
          <div className="relative">
            <Button size="sm" variant="outline" onClick={() => setQuotePickerOpen(o => !o)} className="gap-1.5">
              <FolderOpen className="h-4 w-4" /> Load Quote
            </Button>
            {quotePickerOpen && <div className="fixed inset-0 z-40" onClick={() => setQuotePickerOpen(false)} />}
            {quotePickerOpen && (
              <div className="absolute top-full right-0 mt-1 z-50 w-96 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-lg shadow-lg max-h-96 overflow-hidden flex flex-col">
                <div className="p-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by number, customer, company, reference…"
                      className="pl-8 h-9 text-sm"
                      value={quotePickerSearch}
                      onChange={e => setQuotePickerSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="overflow-y-auto flex-1">
                  {savedQuotes.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-6">No saved quotes</p>
                  )}
                  {savedQuotes
                    .filter(q => {
                      const needle = quotePickerSearch.trim().toLowerCase();
                      if (!needle) return true;
                      return (
                        q.quote_number.toLowerCase().includes(needle) ||
                        (q.customer_name ?? '').toLowerCase().includes(needle) ||
                        (q.customer_company ?? '').toLowerCase().includes(needle) ||
                        (q.reference ?? '').toLowerCase().includes(needle) ||
                        q.status.toLowerCase().includes(needle)
                      );
                    })
                    .slice(0, 50)
                    .map(q => {
                      const customer = q.customer_company || q.customer_name || '—';
                      const buildCount = (q.build_ids ?? []).length;
                      const dt = new Date(q.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
                      return (
                        <button
                          key={q.id}
                          className="w-full text-left px-3 py-2.5 hover:bg-muted/50 border-b last:border-b-0 text-sm flex items-start gap-2"
                          onClick={() => loadQuote(q.id)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-semibold text-primary">{q.quote_number}</span>
                              <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full', STATUS_CLASS[q.status])}>
                                {STATUS_LABEL[q.status]}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground truncate mt-0.5">{customer}</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              {buildCount} build{buildCount === 1 ? '' : 's'} · {q.quote_type.toUpperCase()} · {dt}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>
              </div>
            )}
          </div>

          {/* New quote */}
          {quoteId && (
            <Button size="sm" variant="ghost" onClick={resetQuote} className="gap-1.5 text-muted-foreground">
              <Plus className="h-4 w-4" /> New Quote
            </Button>
          )}

          {/* Display toggles + print */}
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-2 border-l pl-2">
              {(['Price', 'Codes', 'Specs', 'EPS'] as const).map(label => {
                const checked = label === 'Price' ? showPrice : label === 'Codes' ? showCodes : label === 'Specs' ? showSpecs : showEps;
                const setter  = label === 'Price' ? setShowPrice : label === 'Codes' ? setShowCodes : label === 'Specs' ? setShowSpecs : setShowEps;
                return (
                  <label key={label} className="flex items-center gap-1.5 text-sm cursor-pointer select-none">
                    <input type="checkbox" checked={checked} onChange={e => setter(e.target.checked)} className="rounded border-gray-300" />
                    {label}
                  </label>
                );
              })}
              <div className="flex items-center gap-1 border-l pl-2">
                <span className="text-sm text-muted-foreground">ETA</span>
                <input type="number" min={1} className="w-14 border rounded px-2 py-1 text-sm text-center bg-background"
                  placeholder="—" value={etaWeeks} onChange={e => setEtaWeeks(e.target.value)} />
                <span className="text-xs text-muted-foreground">wks</span>
              </div>
              <Button variant="outline" size="sm" onClick={handlePrint} className="gap-1.5">
                <Printer className="h-4 w-4" /> Print
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ── Status action bar (when saved) ──────────────────────────────── */}
      {quoteId && (
        <div className="flex items-center gap-3 px-4 py-2 rounded-lg border bg-muted/30 text-sm">
          <span className="text-muted-foreground font-medium">Status:</span>
          <span className={cn('font-semibold px-2 py-0.5 rounded-full text-xs', STATUS_CLASS[quoteStatus])}>
            {STATUS_LABEL[quoteStatus]}
          </span>
          <div className="flex items-center gap-1.5 ml-auto">
            {quoteStatus === 'draft' && (
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs"
                onClick={() => updateStatusMutation.mutate('sent')} disabled={updateStatusMutation.isPending}>
                <Send className="h-3.5 w-3.5" /> Mark as Sent
              </Button>
            )}
            {quoteStatus === 'sent' && (<>
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs text-green-600 border-green-300 hover:bg-green-50"
                onClick={() => updateStatusMutation.mutate('accepted')} disabled={updateStatusMutation.isPending}>
                <CheckCircle className="h-3.5 w-3.5" /> Accept
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => updateStatusMutation.mutate('rejected')} disabled={updateStatusMutation.isPending}>
                <XCircle className="h-3.5 w-3.5" /> Reject
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                onClick={() => updateStatusMutation.mutate('expired')} disabled={updateStatusMutation.isPending}>
                <Clock className="h-3.5 w-3.5" /> Expire
              </Button>
            </>)}
            {['accepted', 'rejected', 'expired'].includes(quoteStatus) && (
              <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs"
                onClick={() => updateStatusMutation.mutate('sent')} disabled={updateStatusMutation.isPending}>
                Reopen as Sent
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ── Customer & quote details ─────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3 pt-5 px-6">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <User className="h-4 w-4" /> Quote Details
          </CardTitle>
        </CardHeader>
        <CardContent className="px-6 pb-5">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            {/* Left column: customer */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input placeholder="Customer name" value={customerName}
                  onChange={e => setCustomerName(e.target.value)} className="h-8 text-sm" />
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input placeholder="Company (optional)" value={customerCompany}
                  onChange={e => setCustomerCompany(e.target.value)} className="h-8 text-sm" />
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input placeholder="Email (optional)" type="email" value={customerEmail}
                  onChange={e => setCustomerEmail(e.target.value)} className="h-8 text-sm" />
              </div>
            </div>
            {/* Right column: quote meta */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Hash className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input placeholder="Reference / PO number (optional)" value={reference}
                  onChange={e => setReference(e.target.value)} className="h-8 text-sm" />
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex items-center gap-2 flex-1">
                  <label className="text-sm text-muted-foreground whitespace-nowrap">Valid until</label>
                  <Input type="date" value={validUntil}
                    onChange={e => setValidUntil(e.target.value)} className="h-8 text-sm flex-1" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex items-center gap-2 flex-1">
                  <label className="text-sm text-muted-foreground whitespace-nowrap">Discount</label>
                  <Input type="number" min={0} max={100} step={0.5} value={discountPct || ''}
                    onChange={e => setDiscountPct(parseFloat(e.target.value) || 0)}
                    placeholder="0" className="h-8 text-sm w-20" />
                  <span className="text-sm text-muted-foreground">%</span>
                  {discountPct > 0 && (
                    <span className="text-xs text-green-600 font-medium">
                      −{formatCurrency(pricingSummary.discountAmount)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Hash className="h-4 w-4 text-muted-foreground shrink-0 mt-2" />
                <Input placeholder="Notes / custom terms (optional)" value={notes}
                  onChange={e => setNotes(e.target.value)} className="h-8 text-sm" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Build picker ─────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex flex-wrap gap-3 items-start">
            {selectedIds.map((id, index) => {
              const build = allBuilds.find(b => b.id === id);
              return (
                <div key={id} className="flex items-center gap-1 bg-primary/10 rounded-lg px-3 py-2">
                  <span className="font-mono text-sm font-medium">{build?.sku}</span>
                  <button onClick={() => removeBuild(index)} className="ml-1 hover:text-destructive">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
            {selectedIds.length < MAX_BUILDS && (
              <div className="relative">
                <Button variant="outline" size="sm" onClick={() => setBuildPickerOpen(!buildPickerOpen)} className="gap-1">
                  <Plus className="h-4 w-4" /> Add Build
                </Button>
                {buildPickerOpen && (
                  <div className="absolute top-full left-0 mt-1 z-50 w-80 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input placeholder="Search by SKU or description…" className="pl-8 h-9 text-sm"
                          value={searchInput} onChange={e => setSearchInput(e.target.value)} autoFocus />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-56">
                      {allBuilds
                        .filter(b => {
                          if (selectedIds.includes(b.id)) return false;
                          const q = searchInput.toLowerCase();
                          return !q || b.sku.toLowerCase().includes(q) || (b.description || '').toLowerCase().includes(q);
                        })
                        .slice(0, 50)
                        .map(b => (
                          <button key={b.id} className="w-full text-left px-3 py-2 hover:bg-muted/50 text-sm border-b last:border-b-0"
                            onClick={() => addBuild(b.id)}>
                            <span className="font-mono font-medium text-primary">{b.sku}</span>
                            <span className="text-muted-foreground ml-2 truncate">{b.description}</span>
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {buildPickerOpen && <div className="fixed inset-0 z-40" onClick={() => setBuildPickerOpen(false)} />}
        </CardContent>
      </Card>

      {/* ── Build cards ──────────────────────────────────────────────────── */}
      {selectedBuilds.map(build => {
        const bom       = bomData[build.id] ?? [];
        const totalCost = buildTotals[build.id] ?? 0;
        const rrp       = calcRrp(build.rrp, totalCost);
        const qty       = qtyPerBuild[build.id] ?? 1;
        const wifi      = wifiAddons[build.id] ?? null;
        const bt        = btAddons[build.id]   ?? null;
        const epsRrp    = rrp;
        const categories = new Set<string>();
        bom.forEach(item => categories.add(canonicalCategory(item.category || item.component_type || '')));
        const sortedCats = [...categories].sort((a, b) => categoryRank(a) - categoryRank(b));

        return (
          <Card key={build.id}>
            <CardContent className="pt-5">
              {/* Build header */}
              <div className="flex items-center gap-4 mb-3 pb-3 border-b">
                {mediaData[build.id]
                  ? <img src={mediaData[build.id]!} alt={build.sku} className="w-20 h-20 rounded-lg object-cover" />
                  : <div className="w-20 h-20 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-muted-foreground text-xs">No image</div>
                }
                <div className="flex-1">
                  <p className="font-mono font-bold text-lg text-primary">{build.sku}</p>
                  <input
                    className="text-sm text-muted-foreground w-full bg-transparent border-b border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:border-primary focus:outline-none py-0.5 transition-colors"
                    value={getBuildDesc(build.id, build.description)}
                    onChange={e => setEditedBuildDescs(prev => ({ ...prev, [build.id]: e.target.value }))}
                    placeholder="Build description…"
                  />
                </div>
                {showPrice && (
                  <div className="text-right">
                    <p className="text-2xl font-bold">{formatCurrency(epsRrp)}</p>
                    <p className="text-xs text-muted-foreground">per unit, inc. VAT</p>
                    {qty > 1 && <p className="text-sm font-semibold text-primary mt-0.5">{formatCurrency(epsRrp * qty)} ×{qty}</p>}
                    {(build.rrp == null || build.rrp <= 0) && (
                      <Badge variant="outline" className="text-[10px] mt-1">estimated</Badge>
                    )}
                    {showEps && (
                      <div className="mt-2 space-y-0.5">
                        {EPS_PLANS.map(plan => (
                          <p key={plan.months} className="text-xs text-muted-foreground">
                            <span className="font-semibold text-foreground">{formatCurrency(calcEps(epsRrp, plan.markup, plan.months))}</span>/mo ×{plan.months}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Qty + WiFi/BT options */}
              <div className="flex items-center gap-4 mb-3 pb-3 border-b flex-wrap">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  Qty
                  <input type="number" min={1} value={qty}
                    onChange={e => setQtyPerBuild(prev => ({ ...prev, [build.id]: Math.max(1, parseInt(e.target.value) || 1) }))}
                    className="w-16 border rounded px-2 py-1 text-sm text-center bg-background" />
                </div>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer select-none">
                  <input type="checkbox" checked={wifi} onChange={e => setWifiAddons(prev => ({ ...prev, [build.id]: e.target.checked }))} className="rounded border-gray-300" />
                  <Wifi className="h-3.5 w-3.5 text-blue-500" /> WiFi
                </label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer select-none">
                  <input type="checkbox" checked={bt} onChange={e => setBtAddons(prev => ({ ...prev, [build.id]: e.target.checked }))} className="rounded border-gray-300" />
                  <Bluetooth className="h-3.5 w-3.5 text-purple-500" /> Bluetooth
                </label>
              </div>

              {/* Component table */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="p-2 w-8"></th>
                    <th className="text-left p-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground w-32">Component</th>
                    <th className="text-left p-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground">Description</th>
                    <th className="text-center p-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground w-14">Qty</th>
                    {showCodes && <th className="text-left p-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground w-40">Part No.</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedCats.map(cat => {
                    const items = bom.filter(item => canonicalCategory(item.category || item.component_type || '') === cat);
                    const label = CATEGORY_LABELS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1);
                    return items.map((item, i) => {
                      const visible = isVisible(build.id, item.component_sku);
                      return (
                        <tr key={`${cat}-${i}`} className={cn('border-b hover:bg-muted/20', !visible && 'opacity-40')}>
                          <td className="p-2 text-center">
                            <input type="checkbox" checked={visible} onChange={() => toggleVisible(build.id, item.component_sku)} className="rounded border-gray-300" />
                          </td>
                          <td className="p-2 font-medium text-muted-foreground whitespace-nowrap">{i === 0 ? label : ''}</td>
                          <td className="p-2">
                            <input
                              className="w-full bg-transparent border-b border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:border-primary focus:outline-none py-0.5 transition-colors text-sm"
                              value={getCompDesc(item.component_sku, item.description || item.component_sku)}
                              onChange={e => setCompDesc(item.component_sku, e.target.value)}
                            />
                            {showSpecs && (() => {
                              const { entries, allMissing } = getSpecEntries(item.component_sku);
                              if (entries.length === 0) return null;
                              if (allMissing) return (
                                <div className="text-[11px] mt-0.5 text-orange-500 flex items-center gap-1">⚠ No specs populated</div>
                              );
                              const hasMissing = entries.some(e => e.missing);
                              return (
                                <div className="text-[11px] mt-0.5 leading-tight flex flex-wrap gap-x-1">
                                  {entries.map((e, idx) => (
                                    <span key={idx}>
                                      <span className={e.missing ? 'text-orange-500' : 'text-muted-foreground'}>{e.text}</span>
                                      {idx < entries.length - 1 && <span className="text-muted-foreground/50"> · </span>}
                                    </span>
                                  ))}
                                  {hasMissing && <span className="text-orange-500 text-[10px] ml-1" title="Some specs missing">⚠</span>}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="p-2 text-center text-sm text-muted-foreground">{item.qty_per_build}</td>
                          {showCodes && <td className="p-2 font-mono text-xs text-muted-foreground">{item.component_sku}</td>}
                        </tr>
                      );
                    });
                  })}

                  {/* WiFi row */}
                  <tr className="border-b">
                    <td className="p-2 text-center">
                      <input type="checkbox" checked={wifi} onChange={e => setWifiAddons(prev => ({ ...prev, [build.id]: e.target.checked }))} className="rounded border-gray-300" />
                    </td>
                    <td className="p-2 font-medium text-muted-foreground whitespace-nowrap">
                      <div className="flex items-center gap-1"><Wifi className="h-3.5 w-3.5 text-blue-500" /> WiFi</div>
                    </td>
                    <td className="p-2 text-muted-foreground text-sm"></td>
                    <td className="p-2 text-center font-semibold text-base">{wifi ? '✓' : '✗'}</td>
                    {showCodes && <td className="p-2"></td>}
                  </tr>

                  {/* Bluetooth row */}
                  <tr className="border-b">
                    <td className="p-2 text-center">
                      <input type="checkbox" checked={bt} onChange={e => setBtAddons(prev => ({ ...prev, [build.id]: e.target.checked }))} className="rounded border-gray-300" />
                    </td>
                    <td className="p-2 font-medium text-muted-foreground whitespace-nowrap">
                      <div className="flex items-center gap-1"><Bluetooth className="h-3.5 w-3.5 text-purple-500" /> Bluetooth</div>
                    </td>
                    <td className="p-2 text-muted-foreground text-sm"></td>
                    <td className="p-2 text-center font-semibold text-base">{bt ? '✓' : '✗'}</td>
                    {showCodes && <td className="p-2"></td>}
                  </tr>

                </tbody>
              </table>
            </CardContent>
          </Card>
        );
      })}

      {/* ── Add-ons & Licenses ───────────────────────────────────────────── */}
      {selectedIds.length > 0 && (
        <Card>
          <CardHeader className="pb-3 pt-5 px-6">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <ShoppingBag className="h-4 w-4" /> Add-ons &amp; Licenses
            </CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-5">
            {addons.length > 0 && (
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left p-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground">Description</th>
                    <th className="text-left p-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground w-40">Part No.</th>
                    <th className="text-right p-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground w-28">Price (€)</th>
                    <th className="text-center p-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground w-20">Qty</th>
                    <th className="text-right p-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground w-28">Subtotal</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {addons.map(addon => (
                    <tr key={addon.id} className="border-b hover:bg-muted/20">
                      <td className="p-2">
                        <input className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-primary focus:outline-none py-0.5 text-sm transition-colors"
                          value={addon.description}
                          onChange={e => setAddons(prev => prev.map(a => a.id === addon.id ? { ...a, description: e.target.value } : a))}
                        />
                      </td>
                      <td className="p-2 font-mono text-xs text-muted-foreground">{addon.sku}</td>
                      <td className="p-2">
                        <input type="number" min={0} step={0.01} value={addon.price}
                          onChange={e => setAddons(prev => prev.map(a => a.id === addon.id ? { ...a, price: parseFloat(e.target.value) || 0 } : a))}
                          className="w-full border rounded px-2 py-1 text-sm text-right bg-background" />
                      </td>
                      <td className="p-2">
                        <input type="number" min={1} value={addon.qty}
                          onChange={e => setAddons(prev => prev.map(a => a.id === addon.id ? { ...a, qty: Math.max(1, parseInt(e.target.value) || 1) } : a))}
                          className="w-full border rounded px-2 py-1 text-sm text-center bg-background" />
                      </td>
                      <td className="p-2 text-right text-sm font-medium">{formatCurrency(addon.price * addon.qty)}</td>
                      <td className="p-2 text-center">
                        <button onClick={() => setAddons(prev => prev.filter(a => a.id !== addon.id))}
                          className="text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {addons.length === 0 && (
              <p className="text-sm text-muted-foreground mb-3">No add-ons added yet. Search the component library below to add software licenses, accessories, or any extras.</p>
            )}

            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => openCompPicker()}>
              <Plus className="h-4 w-4" /> Add Component
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Pricing summary ──────────────────────────────────────────────── */}
      {selectedIds.length > 0 && (
        <Card>
          <CardHeader className="pb-3 pt-5 px-6">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Pricing Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-5">
            <table className="w-full text-sm">
              <tbody>
                {pricingSummary.buildLines.map(({ build, rrp, qty, lineBase }) => (
                  <tr key={build.id} className="border-b">
                    <td className="py-1.5">
                      <span className="font-mono font-semibold text-foreground">{build.sku}</span>
                      <span className="text-muted-foreground text-xs ml-2">{getBuildDesc(build.id, build.description)}</span>
                      {qty > 1 && <span className="text-xs text-muted-foreground ml-2">×{qty} @ {formatCurrency(rrp)}</span>}
                    </td>
                    <td className="py-1.5 text-right font-medium">{formatCurrency(lineBase)}</td>
                  </tr>
                ))}

                {discountPct > 0 && (
                  <tr className="border-b">
                    <td className="py-1.5 text-muted-foreground">Subtotal</td>
                    <td className="py-1.5 text-right">{formatCurrency(pricingSummary.subtotal)}</td>
                  </tr>
                )}
                {discountPct > 0 && (
                  <tr className="border-b">
                    <td className="py-1.5 text-green-600">Discount ({discountPct}%)</td>
                    <td className="py-1.5 text-right text-green-600 font-medium">−{formatCurrency(pricingSummary.discountAmount)}</td>
                  </tr>
                )}
                <tr className={addons.length > 0 ? 'border-b' : ''}>
                  <td className="pt-3 text-base font-bold">Total (inc. VAT)</td>
                  <td className="pt-3 text-right text-base font-bold">{formatCurrency(pricingSummary.grandTotal)}</td>
                </tr>
                {addons.length > 0 && (
                  <>
                    <tr className="border-b">
                      <td className="py-1 text-xs text-muted-foreground font-semibold uppercase tracking-wide pt-3" colSpan={2}>
                        Optional Add-ons (not included in total)
                      </td>
                    </tr>
                    {addons.map(addon => (
                      <tr key={addon.id} className="border-b">
                        <td className="py-1.5 text-muted-foreground text-sm">
                          {addon.description || addon.sku}
                          {addon.qty > 1 && <span className="text-xs ml-2">×{addon.qty} @ {formatCurrency(addon.price)}</span>}
                        </td>
                        <td className="py-1.5 text-right text-sm text-muted-foreground">{formatCurrency(addon.price * addon.qty)}</td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ── Empty state ──────────────────────────────────────────────────── */}
      {selectedIds.length === 0 && (
        <div className="flex flex-col items-center justify-center h-48 text-muted-foreground gap-2">
          <FileText className="h-10 w-10 opacity-30" />
          <p>Select a build above to generate a quote</p>
        </div>
      )}

      {/* ── Component picker overlay ─────────────────────────────────────── */}
      {compPickerOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setCompPickerOpen(false)} />
          <div className="fixed z-50 left-1/2 top-1/4 -translate-x-1/2 w-[520px] bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-xl shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex items-center gap-2 font-semibold text-sm">
                <ShoppingBag className="h-4 w-4" /> Add Component / License
              </div>
              <button onClick={() => setCompPickerOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8 h-9 text-sm"
                  placeholder="Search by name, part number, or category…"
                  value={compPickerSearch}
                  onChange={e => setCompPickerSearch(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
            <div className="overflow-y-auto max-h-72">
              {compPickerSearch.trim().length < 1 && (
                <p className="text-sm text-muted-foreground text-center py-6">Start typing to search…</p>
              )}
              {compPickerSearch.trim().length >= 1 && compPickerResults.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">No components found</p>
              )}
              {compPickerResults.map(comp => (
                <button key={comp.part_number} className="w-full text-left px-4 py-2.5 hover:bg-muted/50 border-b last:border-b-0"
                  onClick={() => selectComponent(comp)}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{comp.description}</span>
                    <span className="text-sm font-semibold text-primary ml-2">{formatCurrency(comp.current_price ?? 0)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3">
                    <span className="font-mono">{comp.part_number}</span>
                    {comp.category && <span className="capitalize">{comp.category}</span>}
                    <span className={cn('ml-auto', (comp.available_qty ?? 0) > 0 ? 'text-green-600' : 'text-amber-600')}>
                      {comp.available_qty ?? 0} avail
                    </span>
                    {(comp.on_order_qty ?? 0) > 0 && (
                      <span className="text-blue-500">{comp.on_order_qty} on order</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── Hidden print markup ──────────────────────────────────────────── */}
      {selectedIds.length > 0 && (
        <div ref={printRef} className="hidden">
          {/* Header */}
          <div className="quote-header">
            <img src="/logo-horizontal.png" alt="AOI Gaming PCs" className="quote-logo" />
            <div className="quote-meta">
              <div className="quote-number">{quoteNumber ?? 'DRAFT'}</div>
              <div className="quote-date">
                {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            </div>
          </div>

          {/* Customer block */}
          {(customerName || customerCompany || customerEmail || reference) && (
            <div className="customer-block">
              {(customerName || customerCompany || customerEmail) && (
                <div className="col">
                  <div className="label">Customer</div>
                  {customerCompany && <div className="value">{customerCompany}</div>}
                  {customerName    && <div className="value-light">{customerName}</div>}
                  {customerEmail   && <div className="value-light">{customerEmail}</div>}
                </div>
              )}
              <div className="col">
                {reference && (
                  <>
                    <div className="label">Reference</div>
                    <div className="value">{reference}</div>
                  </>
                )}
                {validUntil && (
                  <div style={{ marginTop: reference ? '8px' : 0 }}>
                    <div className="label">Valid Until</div>
                    <div className="value">{new Date(validUntil + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                  </div>
                )}
                {quoteType === 'b2b' && (
                  <div style={{ marginTop: '8px' }}>
                    <div className="label">Quote Type</div>
                    <div className="value">B2B</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Build sections */}
          {selectedBuilds.map(build => {
            const bom       = bomData[build.id] ?? [];
            const totalCost = buildTotals[build.id] ?? 0;
            const rrp       = calcRrp(build.rrp, totalCost);
            const qty       = qtyPerBuild[build.id] ?? 1;
            const wifi      = wifiAddons[build.id] ?? null;
            const bt        = btAddons[build.id]   ?? null;
            const epsRrp    = rrp;
            const categories = new Set<string>();
            bom.forEach(item => categories.add(canonicalCategory(item.category || item.component_type || '')));
            const sortedCats = [...categories].sort((a, b) => categoryRank(a) - categoryRank(b));

            return (
              <div key={build.id} className="build-section">
                <div className="build-title">
                  {mediaData[build.id]
                    ? <img src={mediaData[build.id]!} alt={build.sku} />
                    : <div className="no-image">No image</div>
                  }
                  <div className="info">
                    <div className="sku">{build.sku}</div>
                    <div className="desc">{getBuildDesc(build.id, build.description)}</div>
                    {qty > 1 && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>Quantity: {qty}</div>}
                  </div>
                  {showPrice && (
                    <div className="price-block">
                      <div className="price">{formatCurrency(epsRrp)}</div>
                      <div className="price-label">per unit, inc. VAT{(build.rrp == null || build.rrp <= 0) ? ' (est.)' : ''}</div>
                      {qty > 1 && (
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#111', marginTop: '4px' }}>
                          {formatCurrency(epsRrp * qty)} ×{qty}
                        </div>
                      )}
                      {showEps && (
                        <div style={{ marginTop: '4px' }}>
                          {EPS_PLANS.map(plan => (
                            <div key={plan.months} className="eps-row">
                              <strong>{formatCurrency(calcEps(epsRrp, plan.markup, plan.months))}</strong>/mo ×{plan.months}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <table>
                  <thead>
                    <tr>
                      <th>Component</th>
                      <th>Description</th>
                      <th>Qty</th>
                      {showCodes && <th>Part No.</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCats.map(cat => {
                      const items = bom.filter(item =>
                        canonicalCategory(item.category || item.component_type || '') === cat &&
                        isVisible(build.id, item.component_sku)
                      );
                      const label = CATEGORY_LABELS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1);
                      if (items.length === 0) return null;
                      return items.map((item, i) => (
                        <tr key={`${cat}-${i}`}>
                          <td className="cat-cell">{i === 0 ? label : ''}</td>
                          <td className="desc-cell">
                            {getCompDesc(item.component_sku, item.description || item.component_sku)}
                            {showSpecs && (() => {
                              const { entries, allMissing } = getSpecEntries(item.component_sku);
                              if (entries.length === 0) return null;
                              if (allMissing) return <div className="spec-line" style={{ color: '#f97316' }}>⚠ No specs populated</div>;
                              return (
                                <div className="spec-line">
                                  {entries.map((e, idx) => (
                                    <span key={idx}>
                                      <span style={e.missing ? { color: '#f97316' } : undefined}>{e.text}</span>
                                      {idx < entries.length - 1 && ' · '}
                                    </span>
                                  ))}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="qty-cell">{item.qty_per_build}</td>
                          {showCodes && <td className="code-cell">{item.component_sku}</td>}
                        </tr>
                      ));
                    })}
                    {/* WiFi row */}
                    <tr>
                      <td className="cat-cell"><span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>WiFi</span></td>
                      <td className="desc-cell"></td>
                      <td className="qty-cell" style={{ fontWeight: 700, fontSize: '13px', color: wifi ? '#16a34a' : '#dc2626' }}>{wifi ? '✓' : '✗'}</td>
                      {showCodes && <td className="code-cell"></td>}
                    </tr>
                    {/* Bluetooth row */}
                    <tr>
                      <td className="cat-cell">Bluetooth</td>
                      <td className="desc-cell"></td>
                      <td className="qty-cell" style={{ fontWeight: 700, fontSize: '13px', color: bt ? '#16a34a' : '#dc2626' }}>{bt ? '✓' : '✗'}</td>
                      {showCodes && <td className="code-cell"></td>}
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })}

          {/* Add-ons section */}
          {addons.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div className="section-title">Add-ons &amp; Licenses</div>
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    {showCodes && <th>Part No.</th>}
                    <th style={{ textAlign: 'center', width: '50px' }}>Qty</th>
                    {showPrice && <th style={{ textAlign: 'right', width: '90px' }}>Price</th>}
                  </tr>
                </thead>
                <tbody>
                  {addons.map(addon => (
                    <tr key={addon.id}>
                      <td className="desc-cell">{addon.description || addon.sku}</td>
                      {showCodes && <td className="code-cell">{addon.sku}</td>}
                      <td className="qty-cell">{addon.qty}</td>
                      {showPrice && <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(addon.price * addon.qty)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pricing summary */}
          {showPrice && (
            <div style={{ marginBottom: '24px' }}>
              <div className="section-title">Pricing Summary</div>
              <table className="summary-table">
                <tbody>
                  {pricingSummary.buildLines.map(({ build, rrp, qty, lineBase }) => (
                    <tr key={build.id}>
                      <td className="label">
                        {build.sku}
                        {getBuildDesc(build.id, build.description) && (
                          <span style={{ fontWeight: 400, fontSize: '10px', marginLeft: '6px', color: '#6b7280' }}>
                            {getBuildDesc(build.id, build.description)}
                          </span>
                        )}
                        {qty > 1 ? ` ×${qty} @ ${formatCurrency(rrp)}` : ''}
                      </td>
                      <td className="amount">{formatCurrency(lineBase)}</td>
                    </tr>
                  ))}
                  {discountPct > 0 && (
                    <tr>
                      <td className="label">Subtotal</td>
                      <td className="amount">{formatCurrency(pricingSummary.subtotal)}</td>
                    </tr>
                  )}
                  {discountPct > 0 && (
                    <tr className="discount-row">
                      <td className="label">Discount ({discountPct}%)</td>
                      <td className="amount">−{formatCurrency(pricingSummary.discountAmount)}</td>
                    </tr>
                  )}
                  <tr className="total-row">
                    <td className="label">Total (inc. VAT)</td>
                    <td className="amount">{formatCurrency(pricingSummary.grandTotal)}</td>
                  </tr>
                  {addons.length > 0 && (
                    <>
                      <tr>
                        <td className="label" style={{ paddingTop: '10px', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af' }} colSpan={2}>
                          Optional Add-ons (not included in total)
                        </td>
                      </tr>
                      {addons.map(addon => (
                        <tr key={addon.id}>
                          <td className="label" style={{ color: '#6b7280' }}>
                            {addon.description || addon.sku}
                            {addon.qty > 1 && ` ×${addon.qty} @ ${formatCurrency(addon.price)}`}
                          </td>
                          <td className="amount" style={{ color: '#6b7280' }}>{formatCurrency(addon.price * addon.qty)}</td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          <div className="footer">
            {etaWeeks && <div>ETA ~ {etaWeeks} week{etaWeeks !== '1' ? 's' : ''}, subject to availability and a 30% deposit.</div>}
            {validUntil
              ? <div>Quote valid until {new Date(validUntil + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.</div>
              : <div>Quote valid for 14 days from issue date.</div>
            }
            {notes && <div style={{ marginTop: '4px', fontStyle: 'italic' }}>{notes}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
